import React, { useState, useMemo } from 'react';
import { Filter } from 'lucide-react';
import type { Badge, DigitalPassport } from '@/types/achievement';
import BadgeCollection from './BadgeCollection';
import BadgeDetailsPopup from './BadgeDetailsPopup';
import BadgeSharePopup from './BadgeSharePopup';
import { useAchievements } from '../../hooks/useAchievements';
import { achievementsMapper } from '../../utils/achievementsMapper';

const BadgesTab: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSkillstree, setSelectedSkillstree] = useState('all');
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const { badges, loading, error, coreCategories, skillstree } = useAchievements();
  
  // Generate categories from API data using slugs
  const allCategories = useMemo(() => {
    const categories = [{ name: 'All Categories', slug: 'all' }];
    if (coreCategories && Array.isArray(coreCategories)) {
      coreCategories.forEach((category: any) => {
        if (category.name && category.slug) {
          categories.push({
            name: category.name,
            slug: category.slug
          });
        }
      });
    }
    return categories;
  }, [coreCategories]);

  // Generate skillstrees from API data, filtered by selected category
  const filteredSkillstrees = useMemo(() => {
    const skillstrees = [{ name: 'All Skillstree', slug: 'all' }];
    
    if (skillstree && Array.isArray(skillstree)) {
      const uniqueSkillstrees = new Map();
      
      skillstree.forEach((tree: any) => {
        if (tree.skillstree_name && tree.slug) {
          // Filter by selected category if not "all"
          if (selectedCategory === 'all' || tree.core_category_slug === selectedCategory) {
            // Use Map to ensure unique skillstrees by slug
            if (!uniqueSkillstrees.has(tree.slug)) {
              uniqueSkillstrees.set(tree.slug, {
                name: tree.skillstree_name,
                slug: tree.slug,
                core_category_slug: tree.core_category_slug
              });
            }
          }
        }
      });
      
      // Convert Map values to array
      uniqueSkillstrees.forEach((value) => {
        skillstrees.push(value);
      });
    }
    
    return skillstrees;
  }, [skillstree, selectedCategory]);

  // Filter badge groups based on selected category and skillstree
  const filteredBadgeGroups = useMemo(() => {
    if (!badges || badges.length === 0) return [];

    let filteredBadges = [...badges.filter(p => p.earned_at !== null)];

    // Filter by category
    if (selectedCategory !== 'all') {
      filteredBadges = filteredBadges.filter(badge => badge?.core_category?.slug === selectedCategory);
    }

    // Filter by skillstree
    if (selectedSkillstree !== 'all') {
      filteredBadges = filteredBadges.filter(badge => badge?.skillstree?.slug === selectedSkillstree);
    }

    return achievementsMapper.groupBadgesByName(filteredBadges);
  }, [badges, selectedCategory, selectedSkillstree, achievementsMapper]);

  // Reset skillstree filter when category changes
  const handleCategoryChange = (categorySlug: string) => {
    setSelectedCategory(categorySlug);
    setSelectedSkillstree('all'); // Reset skillstree filter
  };

  const handleBadgeClick = (badge: Badge) => {
    setSelectedBadge(badge);
    setIsDetailsOpen(true);
  };

  const handleShareClick = (badge: Badge) => {
    setSelectedBadge(badge);
    setIsDetailsOpen(false);
    setIsShareOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedBadge(null);
  };

  const handleCloseShare = () => {
    setIsShareOpen(false);
    setSelectedBadge(null);
  };  

  if (loading) return <div>Loading badges...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <section className="badges-tab space-y-8">
        <div className="flex flex-col items-center justify-center">
          <h2 className="text-xl text-white mb-2">Your Badge Collection</h2>
          <p className="text-sm text-white/60 leading-relaxed">
            Earn badges by completing Individual Microskills. Each badge represents mastery of a specific skill area.
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:space-x-4">
          <div className="flex items-center">
            <Filter size={18} className=" text-white/60" />
            <span className=" text-white/60 font-normal">Filters:</span>
          </div>
          
          <select 
            className="bg-black/20 border border-white/20 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-auto max-w-xs"
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
          >
            {allCategories.map((category, index) => (
              <option key={index} value={category.slug} className="bg-[#1a1a3a] py-1 px-2">
                {category.name}
              </option>
            ))}
          </select>
          
          <select 
            className="bg-black/20 border border-white/20 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-auto max-w-xs"
            value={selectedSkillstree}
            onChange={(e) => setSelectedSkillstree(e.target.value)}
            disabled={filteredSkillstrees.length === 1} // Disable if only "All Skillstree" is available
          >
            {filteredSkillstrees.map((skillstree, index) => (
              <option key={index} value={skillstree.slug} className="bg-[#1a1a3a] py-1 px-2">
                {skillstree.name}
              </option>
            ))}
          </select>
        </div>

        <BadgeCollection 
          badgeCategories={filteredBadgeGroups}
          selectedCategory={selectedCategory}
          onBadgeClick={handleBadgeClick}
        />
      </section>

      {/* Badge Details Popup */}
      <BadgeDetailsPopup
        badge={selectedBadge}
        isOpen={isDetailsOpen}
        onClose={handleCloseDetails}
        // onShare={handleShareClick}
      />

      {/* Badge Share Popup */}
      <BadgeSharePopup
        badge={selectedBadge}
        isOpen={isShareOpen}
        onClose={handleCloseShare}
      />
    </>
  );
};

export default BadgesTab;